/**
 * Load More button component for Brands
 * Loads the next page of brands and appends them to the grid.
 * On fresh load at page>1, also fetches the earlier pages so all are shown.
 */
class LoadMoreBrands extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('a');
    this.sectionId = this.dataset.sectionId;
    if (this.button) {
      this.button.addEventListener('click', this.onLoadMore.bind(this));
    }
    this.restoreEarlierPages();
  }

  // Shopify namespaces the pagination param (e.g. "page_63acf68c").
  // Find whichever key starts with "page" in a param string.
  getPageKey(params) {
    for (const key of params.keys()) {
      if (key === 'page' || key.startsWith('page_')) return key;
    }
    return 'page';
  }

  async restoreEarlierPages() {
    const urlParams = new URLSearchParams(window.location.search);
    const pageKey = this.getPageKey(urlParams);
    const currentPage = parseInt(urlParams.get(pageKey) || '1', 10);
    if (currentPage <= 1) return;

    const currentGrid = document.querySelector(`#brands-grid-${this.sectionId}`);
    if (!currentGrid) return;

    // Fetch pages currentPage-1 .. 1 and prepend each, preserving order.
    for (let p = currentPage - 1; p >= 1; p--) {
      try {
        const res = await fetch(
          `${window.location.pathname}?section_id=${this.sectionId}&${pageKey}=${p}`
        );
        const html = await res.text();
        const dom = new DOMParser().parseFromString(html, 'text/html');
        const earlierGrid = dom.querySelector('.brands-grid');
        if (earlierGrid) {
          currentGrid.prepend(...earlierGrid.children);
        }
      } catch (e) {
        /* ignore a failed earlier page */
      }
    }
  }

  onLoadMore(event) {
    event.preventDefault();

    const button = this.button;
    const nextUrl = button.href;

    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');
    button.textContent = button.dataset.loadingText || 'Loading...';

    // Parse the namespaced page param straight from the button's href.
    const query = nextUrl.indexOf('?') === -1
      ? ''
      : nextUrl.slice(nextUrl.indexOf('?') + 1);
    const searchParams = new URLSearchParams(query);
    const pageKey = this.getPageKey(searchParams);
    const nextPage = searchParams.get(pageKey) || '2';

    const sectionId = this.dataset.sectionId;
    const fetchUrl = `${window.location.pathname}?section_id=${sectionId}&${pageKey}=${nextPage}`;

    fetch(fetchUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const parsedDom = new DOMParser().parseFromString(responseText, 'text/html');

        const newGrid = parsedDom.querySelector('.brands-grid');
        const currentGrid = document.querySelector(`#brands-grid-${sectionId}`);

        if (newGrid && currentGrid) {
          const nodes = [...newGrid.children];
          nodes.forEach((node) => node.classList?.add('load-more-new-card'));
          currentGrid.append(...nodes);
        }

        const newLoadMore = parsedDom.querySelector('load-more-brands');
        if (newLoadMore) {
          this.innerHTML = newLoadMore.innerHTML;
          this.button = this.querySelector('a');
          if (this.button) {
            this.button.addEventListener('click', this.onLoadMore.bind(this));
          }
        } else {
          this.remove();
        }

        // Keep a clean, accurate URL using the same namespaced key.
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}?${pageKey}=${nextPage}`
        );
      })
      .catch(() => {
        button.classList.remove('loading');
        button.setAttribute('aria-busy', 'false');
        button.textContent = button.dataset.originalText || 'Load more';
      });
  }
}

customElements.define('load-more-brands', LoadMoreBrands);